import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorIcon } from '../icons/ErrorIcon';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
  onResetKeysChange?: (
    prevResetKeys: Array<string | number> | undefined,
    resetKeys: Array<string | number> | undefined,
  ) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch and handle React component errors gracefully.
 *
 * This component implements React's Error Boundary pattern to prevent the entire
 * application from crashing when a component throws an error. It provides:
 * - User-friendly error display
 * - Error logging
 * - Recovery mechanism (reset button)
 * - Support for reset keys to automatically recover from certain errors
 *
 * @example
 * ```tsx
 * <ErrorBoundary onError={(error, errorInfo) => console.error(error, errorInfo)}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Store error info in state for display
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to send this to an error reporting service
    // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when resetKeys change
    if (hasError && prevProps.resetKeys !== resetKeys) {
      if (this.props.onResetKeysChange) {
        this.props.onResetKeysChange(prevProps.resetKeys, resetKeys);
      }
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
          <div className="card bg-base-100 shadow-xl max-w-2xl w-full">
            <div className="card-body">
              <div className="flex items-center gap-4 mb-4">
                <ErrorIcon />
                <h2 className="card-title text-2xl text-error">
                  Something went wrong
                </h2>
              </div>

              <div className="alert alert-error mb-4">
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="stroke-current flex-shrink-0 h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    An unexpected error occurred. Please try refreshing the page
                    or contact support if the problem persists.
                  </span>
                </div>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="collapse collapse-arrow bg-base-200 mb-4">
                  <input type="checkbox" />
                  <div className="collapse-title text-sm font-medium">
                    Error Details (Development Only)
                  </div>
                  <div className="collapse-content">
                    <div className="text-sm">
                      <p className="font-bold mb-2 text-error">
                        {this.state.error.toString()}
                      </p>
                      {this.state.errorInfo && (
                        <pre className="bg-base-300 p-2 rounded text-xs overflow-auto max-h-96">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="card-actions justify-end">
                <button
                  className="btn btn-primary"
                  onClick={this.resetErrorBoundary}
                  data-testid="error-boundary-reset-button"
                >
                  Try Again
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => window.location.reload()}
                  data-testid="error-boundary-reload-button"
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook to manually trigger error boundary from functional components.
 * Use this when you need to handle errors that Error Boundary can't catch
 * (e.g., errors in event handlers, async code, etc.)
 */
export const useErrorHandler = () => {
  return (error: Error, _errorInfo?: ErrorInfo) => {
    // In a real implementation, you might want to use a state management solution
    // or context to trigger the error boundary
    throw error;
  };
};
